import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, UserCheck, UserX, Plus, Lock } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasNonSelfPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { formatEmployeeName } from '@/lib/format';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useLabel } from '@/lib/labels';
import { EmployeeAffiliationEditor } from '@/components/EmployeeAffiliationEditor';
import { SkillMatrixEditor } from '@/components/SkillMatrixEditor';
import { MasterHistoryPanel } from '@/components/masters/MasterHistoryPanel';

/**
 * `/employees/:id` (v0.4 章⑤ F-5、実質新規の社員詳細ページ)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-2 / §5-4 / §5-5 / §5-10
 *
 * ⚠️ D-11 (可視性ルール): 「給与単価」タブは compensation:view の display_level が amount 未満だと
 * GET 自体が403になる (フィールド単位の欠落ではなく、この画面では endpoint 単位の可否として現れる)。
 * 403 は権限不足のエラーではなく「見せない設定」として扱い、タブ内に穏当な説明を出す。
 * ⚠️ D-209 の教訓: タブごとに要求する feature が異なる (user/skill/compensation/income_cap)。
 *    タブの表示可否・保存可否は必ずタブ単位でバックエンドの要求 feature と対応させて判定すること。
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type EmployeeDetail = {
  employee_id: string;
  has_linked_user: boolean;
  employee_number: string;
  family_name: string;
  given_name: string;
  family_name_kana: string | null;
  given_name_kana: string | null;
  middle_name: string | null;
  middle_name_kana: string | null;
  employee_category_id: string | null;
  employment_type_id: string | null;
  hire_date: string;
  termination_date: string | null;
  status: string;
  // v0.4 TD-25 (D-261): 個人上書き。null = 組織ノードの標準値 (設定 > 勤怠・法令設定) に従う
  weekly_contracted_work_days: number | null;
  weekly_contracted_minutes: number | null;
  // v0.4 追加要件⑩ D-328 再設計 (D-335): null = 店舗のシフト編成の対象 (従来通り)
  default_work_pattern_type_id: string | null;
  // v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-2/§3-3): null = 未設定
  grade_id: string | null;
  working_time_contract_class_id: string | null;
  daily_scheduled_minutes_override: number | null;
  monthly_scheduled_minutes_override: number | null;
  // v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-4): 生年月日。閲覧・編集は company_admin のみ
  // (このページ自体が isCompanyAdmin 必須のため、住所・緊急連絡先と同じ扱い)。詳細取得でのみ返る
  birth_date: string | null;
  // TD-28 段階⑦ (D-14): 住所・緊急連絡先。詳細取得 (GET /employees/:id) でのみ返る
  address_postal_code: string | null;
  address_prefecture: string | null;
  address_city: string | null;
  address_line: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  effective_from: string;
};

type Category = { category_id: string; code: string; label: string };
type EmploymentType = { type_id: string; code: string; label: string };
type DefaultWorkPatternType = { pattern_type_id: string; code: string; label: string };
// v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-2/§3-3)
type EmployeeGrade = { grade_id: string; code: string; label: string };
type WorkingTimeContractClass = { contract_class_id: string; code: string; label: string };

/** v0.5 マスタ変更履歴ビューア フェーズ1 §2-4: employeeVersions (基本情報) の表示順・ラベル。
 *  給与単価・家族情報・年収の壁は対象外 (§0-4、フェーズ2以降)。 */
const EMPLOYEE_HISTORY_FIELD_LABELS: Record<string, string> = {
  employee_number: '社員番号', family_name: '姓', given_name: '名',
  family_name_kana: '姓（かな）', given_name_kana: '名（かな）', middle_name: 'ミドル', middle_name_kana: 'ミドルかな',
  employee_category_id: '社員区分ID', employment_type_id: '雇用形態ID',
  hire_date: '入社日', termination_date: '退職日', status: 'ステータス',
  weekly_contracted_work_days: '週所定労働日数', weekly_contracted_minutes: '週所定労働時間(分)',
  default_work_pattern_type_id: '既定勤務パターンID',
  grade_id: 'グレードID', working_time_contract_class_id: '契約区分ID',
  daily_scheduled_minutes_override: '1日の所定労働時間 (分、個別上書き)', monthly_scheduled_minutes_override: '月の所定労働時間 (分、個別上書き)',
  birth_date: '生年月日',
  address_postal_code: '郵便番号', address_prefecture: '都道府県', address_city: '市区町村', address_line: '番地以降',
  emergency_contact_name: '緊急連絡先氏名', emergency_contact_phone: '緊急連絡先電話番号', emergency_contact_relation: '緊急連絡先続柄',
};

function StatusBadge({ status }: { status: string }) {
  return status === 'active' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-success-bg text-success-text">
      <UserCheck size={12} /> 在職
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-surface-sunken text-text-sub">
      <UserX size={12} /> 退職
    </span>
  );
}

// -----------------------------------------------------------------------------
// タブ1: 基本情報
// -----------------------------------------------------------------------------
function BasicInfoTab({ employee }: { employee: EmployeeDetail }) {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: categories = [] } = useQuery<Category[]>({ queryKey: ['employee-categories'], queryFn: () => apiFetch<Category[]>('/employee-categories') });
  const { data: employmentTypes = [] } = useQuery<EmploymentType[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types') });
  // v0.4 追加要件⑩ D-328 再設計 (D-335): 「調整不要な既定の勤務パターン」マスタ (0件でも正当、§6-0 原則3)
  const { data: patternTypesData } = useQuery<{ pattern_types: DefaultWorkPatternType[] }>({
    queryKey: ['default-work-pattern-types'],
    queryFn: () => apiFetch('/settings/default-work-pattern-types'),
  });
  const patternTypes = patternTypesData?.pattern_types ?? [];
  // v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-2/§3-3): Full限定マスタ (0件でも正当、§6-0原則3)
  const { data: grades = [] } = useQuery<EmployeeGrade[]>({ queryKey: ['employee-grades'], queryFn: () => apiFetch<EmployeeGrade[]>('/masters/employee-grades') });
  const { data: contractClasses = [] } = useQuery<WorkingTimeContractClass[]>({
    queryKey: ['working-time-contract-classes'], queryFn: () => apiFetch<WorkingTimeContractClass[]>('/masters/working-time-contract-classes'),
  });

  const [form, setForm] = useState({
    employee_number: employee.employee_number,
    family_name: employee.family_name,
    given_name: employee.given_name,
    family_name_kana: employee.family_name_kana ?? '',
    given_name_kana: employee.given_name_kana ?? '',
    middle_name: employee.middle_name ?? '',
    middle_name_kana: employee.middle_name_kana ?? '',
    employee_category_id: employee.employee_category_id ?? '',
    employment_type_id: employee.employment_type_id ?? '',
    hire_date: employee.hire_date,
    weekly_contracted_work_days: employee.weekly_contracted_work_days != null ? String(employee.weekly_contracted_work_days) : '',
    weekly_contracted_minutes: employee.weekly_contracted_minutes != null ? String(employee.weekly_contracted_minutes) : '',
    default_work_pattern_type_id: employee.default_work_pattern_type_id ?? '',
    grade_id: employee.grade_id ?? '',
    working_time_contract_class_id: employee.working_time_contract_class_id ?? '',
    daily_scheduled_minutes_override: employee.daily_scheduled_minutes_override != null ? String(employee.daily_scheduled_minutes_override) : '',
    monthly_scheduled_minutes_override: employee.monthly_scheduled_minutes_override != null ? String(employee.monthly_scheduled_minutes_override) : '',
    birth_date: employee.birth_date ?? '',
    address_postal_code: employee.address_postal_code ?? '',
    address_prefecture: employee.address_prefecture ?? '',
    address_city: employee.address_city ?? '',
    address_line: employee.address_line ?? '',
    emergency_contact_name: employee.emergency_contact_name ?? '',
    emergency_contact_phone: employee.emergency_contact_phone ?? '',
    emergency_contact_relation: employee.emergency_contact_relation ?? '',
    effective_from: new Date().toISOString().slice(0, 10),
  });

  const updateMut = useMutation({
    mutationFn: () => apiFetch(`/employees/${employee.employee_id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        employee_number: form.employee_number.trim(),
        family_name: form.family_name.trim(),
        given_name: form.given_name.trim(),
        family_name_kana: form.family_name_kana.trim() || null,
        given_name_kana: form.given_name_kana.trim() || null,
        middle_name: form.middle_name.trim() || null,
        middle_name_kana: form.middle_name_kana.trim() || null,
        employee_category_id: form.employee_category_id || null,
        employment_type_id: form.employment_type_id || null,
        hire_date: form.hire_date,
        weekly_contracted_work_days: form.weekly_contracted_work_days === '' ? null : Number(form.weekly_contracted_work_days),
        weekly_contracted_minutes: form.weekly_contracted_minutes === '' ? null : Number(form.weekly_contracted_minutes),
        default_work_pattern_type_id: form.default_work_pattern_type_id || null,
        grade_id: form.grade_id || null,
        working_time_contract_class_id: form.working_time_contract_class_id || null,
        daily_scheduled_minutes_override: form.daily_scheduled_minutes_override === '' ? null : Number(form.daily_scheduled_minutes_override),
        monthly_scheduled_minutes_override: form.monthly_scheduled_minutes_override === '' ? null : Number(form.monthly_scheduled_minutes_override),
        birth_date: form.birth_date || null,
        address_postal_code: form.address_postal_code.trim() || null,
        address_prefecture: form.address_prefecture.trim() || null,
        address_city: form.address_city.trim() || null,
        address_line: form.address_line.trim() || null,
        emergency_contact_name: form.emergency_contact_name.trim() || null,
        emergency_contact_phone: form.emergency_contact_phone.trim() || null,
        emergency_contact_relation: form.emergency_contact_relation.trim() || null,
        effective_from: form.effective_from,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employee', employee.employee_id] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      toast.success('社員情報を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  return (
    <form
      className="space-y-4 max-w-2xl"
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.family_name.trim() || !form.given_name.trim()) { toast.error('氏名を入力してください'); return; }
        updateMut.mutate();
      }}
    >
      <div className="flex justify-end">
        <MasterHistoryPanel entityRoute="/employees" id={employee.employee_id} label="社員（基本情報）" fieldLabels={EMPLOYEE_HISTORY_FIELD_LABELS} variant="button" />
      </div>
      {employee.status === 'terminated' && (
        <p className="text-xs text-alert-text bg-alert-bg border border-alert-border/40 rounded p-2">
          この社員は退職済みです。保存すると再雇用として履歴に記録され「在職」に戻ります。
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[5fr_2fr_5fr] gap-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.family_name', '姓 (Family)')} <span className="text-danger-text">*</span></label>
          <input className={inputCls} required value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.middle_name', 'ミドル (任意)')}</label>
          <input className={inputCls} value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.given_name', '名 (Given)')} <span className="text-danger-text">*</span></label>
          <input className={inputCls} required value={form.given_name} onChange={(e) => setForm({ ...form, given_name: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[5fr_2fr_5fr] gap-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.family_name_kana', '姓（かな）')}</label>
          <input className={inputCls} value={form.family_name_kana} onChange={(e) => setForm({ ...form, family_name_kana: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.middle_name_kana', 'ミドルかな（任意）')}</label>
          <input className={inputCls} value={form.middle_name_kana} onChange={(e) => setForm({ ...form, middle_name_kana: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.given_name_kana', '名（かな）')}</label>
          <input className={inputCls} value={form.given_name_kana} onChange={(e) => setForm({ ...form, given_name_kana: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="block text-sm text-text-sub mb-1">{t('field.employees.employee_number', '社員番号')} <span className="text-danger-text">*</span></label>
        <input className={`${inputCls} font-mono`} required value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.employee_category_id', '社員区分')}</label>
          <Select value={form.employee_category_id || undefined} onValueChange={(v) => setForm({ ...form, employee_category_id: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="— 未設定 —" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.employment_type_id', '雇用形態')}</label>
          <Select value={form.employment_type_id || undefined} onValueChange={(v) => setForm({ ...form, employment_type_id: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="— 未設定 —" /></SelectTrigger>
            <SelectContent>{employmentTypes.map((et) => <SelectItem key={et.type_id} value={et.type_id}>{et.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">{t('field.employees.hire_date', '入社日')} <span className="text-danger-text">*</span></label>
          <input type="date" className={inputCls} required value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">稼働開始日 <span className="text-danger-text">*</span></label>
          <input type="date" className={inputCls} required value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          <p className="text-xs text-text-sub mt-1">この変更が反映される日付（通常は本日）</p>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-body mb-1">所定労働日数・時間 (個別契約)</h3>
        <p className="text-xs text-text-sub mb-2">個別の雇用契約が無い場合は空欄のままにしてください。空欄の場合は所属店舗 (組織ノード) の標準値が適用されます (「設定 &gt; 勤怠・法令設定」で確認・設定できます)。</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">週所定労働日数</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
                value={form.weekly_contracted_work_days}
                onChange={(e) => setForm({ ...form, weekly_contracted_work_days: e.target.value })}
              />
              <span className="text-xs text-text-sub">日/週</span>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">週所定労働時間 (分)</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
                value={form.weekly_contracted_minutes}
                onChange={(e) => setForm({ ...form, weekly_contracted_minutes: e.target.value })}
              />
              <span className="text-xs text-text-sub">分/週</span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-body mb-1">既定勤務パターン</h3>
        <p className="text-xs text-text-sub mb-2">
          店舗のシフト編成を必要としない社員 (本社勤務者等) の場合のみ設定してください。パターンの内容は
          「設定 &gt; 既定勤務パターン」で登録・編集できます。
          <span className="block mt-1 text-alert-text">
            ⚠️ この設定を保存すると、正式な予定として自動登録され、店舗のシフト編成の対象からも自動的に外れます。
          </span>
        </p>
        <Select value={form.default_work_pattern_type_id || undefined} onValueChange={(v) => setForm({ ...form, default_work_pattern_type_id: v })}>
          <SelectTrigger className="w-full sm:w-80 min-h-[44px]"><SelectValue placeholder="— 店舗のシフト編成の対象 —" /></SelectTrigger>
          <SelectContent>{patternTypes.map((pt) => <SelectItem key={pt.pattern_type_id} value={pt.pattern_type_id}>{pt.label}</SelectItem>)}</SelectContent>
        </Select>
        {form.default_work_pattern_type_id && (
          <button type="button" onClick={() => setForm({ ...form, default_work_pattern_type_id: '' })}
            className="mt-1 min-h-[44px] text-xs text-text-sub hover:text-text-body hover:underline">
            設定を解除する (店舗のシフト編成の対象に戻す)
          </button>
        )}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-body mb-1">グレード・契約区分</h3>
        <p className="text-xs text-text-sub mb-2">
          いずれも未設定のままにできます。マスタの登録・編集は「マスタ &gt; グレード」「マスタ &gt; 契約区分」で行います。
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">グレード</label>
            <Select value={form.grade_id || undefined} onValueChange={(v) => setForm({ ...form, grade_id: v })}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="— 未設定 —" /></SelectTrigger>
              <SelectContent>{grades.map((g) => <SelectItem key={g.grade_id} value={g.grade_id}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
            {/* design-expertレビュー Medium対応: 「未設定のままにできる」という上の説明文と裏腹に
                解除手段が無かった (default_work_pattern_type_id には既にある) */}
            {form.grade_id && (
              <button type="button" onClick={() => setForm({ ...form, grade_id: '' })}
                className="mt-1 min-h-[44px] text-xs text-text-sub hover:text-text-body hover:underline">
                設定を解除する
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">契約区分</label>
            <Select value={form.working_time_contract_class_id || undefined} onValueChange={(v) => setForm({ ...form, working_time_contract_class_id: v })}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="— 未設定 —" /></SelectTrigger>
              <SelectContent>{contractClasses.map((cc) => <SelectItem key={cc.contract_class_id} value={cc.contract_class_id}>{cc.label}</SelectItem>)}</SelectContent>
            </Select>
            {form.working_time_contract_class_id && (
              <button type="button" onClick={() => setForm({ ...form, working_time_contract_class_id: '' })}
                className="mt-1 min-h-[44px] text-xs text-text-sub hover:text-text-body hover:underline">
                設定を解除する
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-text-sub mt-2 mb-1">個別の雇用契約がある社員は、契約区分マスタの標準値をここで上書きできます (空欄なら契約区分の標準値に従います)。</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">1日の所定労働時間 (分、個別上書き)</label>
            <input type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
              value={form.daily_scheduled_minutes_override} onChange={(e) => setForm({ ...form, daily_scheduled_minutes_override: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">月の所定労働時間 (分、個別上書き)</label>
            <input type="number" min={1} className={`${numberInputCls} max-w-[8rem]`}
              value={form.monthly_scheduled_minutes_override} onChange={(e) => setForm({ ...form, monthly_scheduled_minutes_override: e.target.value })} />
          </div>
        </div>
      </div>
      <div>
        {/* design-expertレビュー Major対応: 見出しのみでlabel要素が無く、スクリーンリーダーに
            アクセシブルネームが伝わらなかった (WCAG 4.1.2) */}
        <label htmlFor="emp-birth-date" className="block text-sm font-semibold text-text-body mb-1">生年月日</label>
        <p className="text-xs text-text-sub mb-2">この画面は会社管理者のみが閲覧できます (社員一覧・年少者判定以外の画面には表示されません)。</p>
        <input id="emp-birth-date" type="date" className={`${inputCls} max-w-[12rem]`} autoComplete="off"
          value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-body mb-1">住所</h3>
        <p className="text-xs text-text-sub mb-2">
          社員本人からの個人情報変更申請が承認された場合もここに反映されます。この画面は会社管理者のみが閲覧できます
          (社員一覧には住所・緊急連絡先を含めていません)。
        </p>
        {/* ⚠️ 他人の情報を編集する画面のため autoComplete を明示的に切る
            (操作者自身の住所がブラウザ補完候補に出て、別人のレコードへ混入する事故を防ぐ) */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[3fr_4fr] gap-3">
            <div>
              <label htmlFor="emp-address-postal" className="block text-sm text-text-sub mb-1">{t('field.employees.address_postal_code', '郵便番号')}</label>
              <input id="emp-address-postal" className={inputCls} maxLength={16} inputMode="numeric" autoComplete="off"
                value={form.address_postal_code} onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })} />
            </div>
            <div>
              <label htmlFor="emp-address-pref" className="block text-sm text-text-sub mb-1">{t('field.employees.address_prefecture', '都道府県')}</label>
              <input id="emp-address-pref" className={inputCls} maxLength={50} autoComplete="off"
                value={form.address_prefecture} onChange={(e) => setForm({ ...form, address_prefecture: e.target.value })} />
            </div>
          </div>
          <div>
            <label htmlFor="emp-address-city" className="block text-sm text-text-sub mb-1">{t('field.employees.address_city', '市区町村・番地')}</label>
            <input id="emp-address-city" className={inputCls} maxLength={200} autoComplete="off"
              value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} />
          </div>
          <div>
            <label htmlFor="emp-address-line" className="block text-sm text-text-sub mb-1">{t('field.employees.address_line', '建物名・部屋番号等')}</label>
            <input id="emp-address-line" className={inputCls} maxLength={200} autoComplete="off"
              value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} />
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-body mb-2">緊急連絡先</h3>
        {/* ⚠️ 既定ラベルは migration 0095 の label_key_catalog.default_label と文字列まで一致させること。
            食い違うと「設定 > 用語のカスタマイズ」の表示と実画面がずれる (§8-55/§8-93 型)。 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="emp-emg-name" className="block text-sm text-text-sub mb-1">{t('field.employees.emergency_contact_name', '緊急連絡先氏名')}</label>
            <input id="emp-emg-name" className={inputCls} maxLength={100} autoComplete="off"
              value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="emp-emg-phone" className="block text-sm text-text-sub mb-1">{t('field.employees.emergency_contact_phone', '緊急連絡先電話番号')}</label>
            <input id="emp-emg-phone" className={inputCls} type="tel" maxLength={30} autoComplete="off"
              value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
          </div>
          <div>
            <label htmlFor="emp-emg-relation" className="block text-sm text-text-sub mb-1">{t('field.employees.emergency_contact_relation', '緊急連絡先続柄')}</label>
            <input id="emp-emg-relation" className={inputCls} maxLength={50} autoComplete="off"
              value={form.emergency_contact_relation} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })} />
          </div>
        </div>
      </div>
      <div className="pt-2">
        <Button type="submit" variant="primary" loading={updateMut.isPending} loadingLabel="保存中…">保存</Button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
// タブ2: 所属
// ⚠️ v0.4 追加要件⑩: EmployeesPage の所属編集モーダルと UI が二重実装されており、片方だけ直すと
// §8-55 型の同期漏れになるため EmployeeAffiliationEditor へ集約した。
// -----------------------------------------------------------------------------
function AffiliationTab({ employeeId }: { employeeId: string }) {
  return (
    <div className="max-w-2xl space-y-3">
      {/* ⚠️ §2-4: 所属自体 (employee_node_assignments) はバージョン管理を持たず履歴が無い。
          ここで開けるのは employeeVersions (基本情報) の履歴 (design doc §2-4)。design-expertレビュー
          指摘 (CRITICAL) を受け、「所属の履歴」と誤解されないようボタン文言と注記で明示する。 */}
      <div className="flex justify-end items-center gap-2 flex-wrap">
        <p className="text-xs text-text-sub">↓ 所属自体の履歴ではなく、氏名・社員番号等の基本情報の変更履歴です</p>
        <MasterHistoryPanel entityRoute="/employees" id={employeeId} label="社員（基本情報）" fieldLabels={EMPLOYEE_HISTORY_FIELD_LABELS} variant="button" triggerLabel="基本情報の履歴" />
      </div>
      <EmployeeAffiliationEditor employeeId={employeeId} />
    </div>
  );
}


// -----------------------------------------------------------------------------
// タブ4: 給与単価 (D-11 可視性ルール: 403は「見せない設定」として扱う)
// -----------------------------------------------------------------------------
type Compensation = { compensation_id: string; node_id: string | null; compensation_type: string; pay_unit: string; amount: number; currency_code: string };

function CompensationTab({ employeeId, canManage }: { employeeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<Compensation[]>({
    queryKey: ['employee-compensations', employeeId],
    queryFn: () => apiFetch(`/employees/${employeeId}/compensations`),
    retry: false,
  });
  const [payUnit, setPayUnit] = useState<'hourly' | 'daily' | 'monthly'>('hourly');
  const [amount, setAmount] = useState('');

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/employees/${employeeId}/compensations`, {
      method: 'PUT', body: JSON.stringify({ node_id: null, compensation_type: 'base_wage', pay_unit: payUnit, amount: Number(amount) }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-compensations', employeeId] }); toast.success('単価を更新しました'); setAmount(''); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const PAY_UNIT_LABELS: Record<string, string> = { hourly: '時給', daily: '日給', monthly: '月給' };

  if (isLoading) return <div className="text-text-sub">読み込み中…</div>;
  if (isError || !data) {
    return <p className="text-sm text-text-sub">この社員の単価情報を閲覧する権限がありません。</p>;
  }

  return (
    <div className="space-y-4 max-w-lg">
      {data.length === 0 ? (
        <p className="text-sm text-text-sub">単価はまだ設定されていません。</p>
      ) : (
        <div className="border border-surface-border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken text-text-sub"><tr><th className="text-left px-3 py-2" scope="col">区分</th><th className="text-left px-3 py-2" scope="col">単価</th></tr></thead>
            <tbody className="divide-y divide-surface-border">
              {data.map((c) => (
                <tr key={c.compensation_id}>
                  <td className="px-3 py-2">{c.node_id ? '店舗別' : '基本'} / {PAY_UNIT_LABELS[c.pay_unit] ?? c.pay_unit}</td>
                  <td className="px-3 py-2 tabular-nums font-semibold">{c.amount.toLocaleString('ja-JP')} 円</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-32">
            <label className="block text-xs text-text-sub mb-1">区分</label>
            <Select value={payUnit} onValueChange={(v) => setPayUnit(v as typeof payUnit)}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">時給</SelectItem>
                <SelectItem value="daily">日給</SelectItem>
                <SelectItem value="monthly">月給</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <label className="block text-xs text-text-sub mb-1">単価 (円)</label>
            <input type="number" min={0} className={numberInputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <Button variant="primary" size="sm" disabled={!amount || Number(amount) <= 0 || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
            保存
          </Button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ5: 年収の壁 (income_cap:manage のみ、self閲覧は別画面 /me/income)
// -----------------------------------------------------------------------------
type IncomeCap = { cap_id: string; cap_year: number; self_declared_cap_amount: number | null; cap_basis_key: string | null; has_multiple_employers: boolean } | null;

function IncomeCapTab({ employeeId }: { employeeId: string }) {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data, isLoading, isError } = useQuery<IncomeCap>({
    queryKey: ['employee-income-cap', employeeId, year],
    queryFn: () => apiFetch(`/employees/${employeeId}/income-cap?year=${year}`),
    retry: false,
  });
  const [capAmount, setCapAmount] = useState('');
  const [hasMultiple, setHasMultiple] = useState(false);

  useEffect(() => {
    setCapAmount(data?.self_declared_cap_amount != null ? String(data.self_declared_cap_amount) : '');
    setHasMultiple(data?.has_multiple_employers ?? false);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/employees/${employeeId}/income-cap`, {
      method: 'PUT',
      body: JSON.stringify({
        cap_year: year, self_declared_cap_amount: capAmount ? Number(capAmount) : null, has_multiple_employers: hasMultiple,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-income-cap', employeeId] }); toast.success('年収上限の設定を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  if (isError) return <p className="text-sm text-text-sub">この社員の年収上限を管理する権限がありません。</p>;

  return (
    <div className="space-y-4 max-w-md">
      <div className="w-32">
        <label className="block text-xs text-text-sub mb-1">対象年</label>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
          <SelectContent>{[currentYear - 1, currentYear, currentYear + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}年</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {isLoading ? <div className="text-text-sub">読み込み中…</div> : (
        <>
          <div>
            <label className="block text-xs text-text-sub mb-1">本人の希望上限 (円、任意)</label>
            <input type="number" min={0} className={numberInputCls} value={capAmount} onChange={(e) => setCapAmount(e.target.value)} placeholder="未設定" />
          </div>
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" checked={hasMultiple} onChange={(e) => setHasMultiple(e.target.checked)} />
            他社と掛け持ちしている
          </label>
          <Button variant="primary" size="sm" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// タブ6: 家族情報 (労務担当向け閲覧、employee_family:view:all_stores のみ・読み取り専用)
//
// ⚠️ v04-leave-management-detail.md §3-4-2 / §13-2 ⑧: `employee_family` に own_store の
//    permission 行は無い (店長を構造的に締め出す)。本タブは all_stores 保有者にしか実効性が
//    無いため EmployeeDetailPage() 側で表示可否を事前判定する (§6-3 D-209)。
// ⚠️ D-273: 表示は family_info_visibility の段階 (none/exists_only/full) で出し分ける。
//    'none' は「権限が無い」ではなく「テナントの可視性ルール設定で非表示」であり、断定的な
//    「データがありません」表示にしない (§6-0 原則3 の裏返し)。編集・同意操作は本タブでは行わない
//    (書込みは本人専用の /me/family のみ、routes/employee-family.ts §5-5)。
// -----------------------------------------------------------------------------
type FamilyInfoLevel = 'none' | 'exists_only' | 'full';
type FamilyMemberView = {
  family_member_id: string;
  relationship_code: string;
  family_name: string | null;
  given_name: string | null;
  birth_date: string | null;
  is_cohabiting: boolean | null;
  is_dependent: boolean | null;
  has_sensitive_care_consent: boolean;
  requires_care_level?: string | null;
};
type FamilyInfoResponse = {
  visibility_level: FamilyInfoLevel;
  count: number;
  relationship_codes: string[];
  family_members: FamilyMemberView[];
};
type FamilyRelationship = { relationship_code: string; label: string };

function FamilyInfoTab({ employeeId }: { employeeId: string }) {
  const t = useLabel();
  const { data, isLoading, isError, error } = useQuery<FamilyInfoResponse>({
    queryKey: ['employee-family-members', employeeId],
    queryFn: () => apiFetch(`/employees/${employeeId}/family-members`),
    retry: false,
  });
  const { data: relData } = useQuery<{ relationships: FamilyRelationship[] }>({
    queryKey: ['family-relationships'],
    queryFn: () => apiFetch('/family-relationships'),
    staleTime: 10 * 60_000,
  });
  const relationshipLabels = useMemo(
    () => new Map((relData?.relationships ?? []).map((r) => [r.relationship_code, r.label])),
    [relData],
  );

  if (isLoading) return <div className="text-text-sub">読み込み中…</div>;
  if (isError) {
    const featureDisabled = error instanceof ApiClientError && error.code === 'FEATURE_DISABLED';
    return (
      <p className="text-sm text-text-sub">
        {featureDisabled ? '家族情報の管理は会社の設定で無効になっています。' : 'この社員の家族情報を閲覧する権限がありません。'}
      </p>
    );
  }
  // ⚠️ §8-60: data === undefined を「データが無い」と断定しない (paused 等でも undefined になる)。
  if (!data) return <p className="text-sm text-text-sub">家族情報を取得できていません。再読み込みしてください。</p>;

  if (data.visibility_level === 'none') {
    return (
      <div className="border border-surface-border rounded-lg bg-white p-4 max-w-2xl">
        <p className="text-sm text-text-body">この情報は現在の設定では表示されません。</p>
        <p className="text-xs text-text-sub mt-1">
          閲覧するには「設定 &gt; 可視性ルール」で家族情報 (family_info_visibility) の表示レベルを設定してください。
        </p>
      </div>
    );
  }

  if (data.visibility_level === 'exists_only') {
    return (
      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-2 max-w-2xl">
        <p className="text-sm text-text-body">{data.count} 件の{t('entity.employee_family_members', '家族情報')}が登録されています。</p>
        {data.relationship_codes.length > 0 && (
          <p className="text-xs text-text-sub">
            {t('field.employee_family_members.relationship_code', '続柄')}: {data.relationship_codes.map((code) => relationshipLabels.get(code) ?? code).join('、')}
          </p>
        )}
        <p className="text-xs text-text-sub">氏名・生年月日などの詳細は現在の設定では表示されません。</p>
      </div>
    );
  }

  if (data.family_members.length === 0) {
    return (
      <div className="border border-surface-border rounded-lg bg-white p-4 max-w-2xl">
        <p className="text-sm text-text-body">登録されている{t('entity.employee_family_members', '家族情報')}はありません。</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 max-w-4xl">
      {data.family_members.map((m) => {
        const name = [m.family_name, m.given_name].filter(Boolean).join(' ') || '(氏名の登録なし)';
        return (
          <div key={m.family_member_id} className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
            <p className="text-xs font-bold text-text-sub">{relationshipLabels.get(m.relationship_code) ?? m.relationship_code}</p>
            <p className="text-card-title text-text-main break-words">{name}</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="flex gap-2">
                <dt className="text-text-sub shrink-0">{t('field.employee_family_members.birth_date', '生年月日')}</dt>
                <dd className="text-text-body break-words">{m.birth_date ?? '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-text-sub shrink-0">同居 / 扶養</dt>
                <dd className="text-text-body">
                  {m.is_cohabiting === true ? '同居' : m.is_cohabiting === false ? '別居' : '—'}
                  {' / '}
                  {m.is_dependent === true ? '扶養あり' : m.is_dependent === false ? '扶養なし' : '—'}
                </dd>
              </div>
            </dl>
            <div className="border-t border-surface-line pt-2">
              {m.has_sensitive_care_consent ? (
                <p className="text-sm text-text-body">
                  <span className="text-text-sub">{t('field.employee_family_members.requires_care_level', '要介護状態')}: </span>
                  {m.requires_care_level ?? '未入力'}
                </p>
              ) : (
                <p className="text-xs text-text-sub">要配慮情報 (要介護状態) は本人の同意が記録されていないため表示されません。</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// メイン
// -----------------------------------------------------------------------------
type TabKey = 'basic' | 'affiliation' | 'skills' | 'compensation' | 'income-cap' | 'family';
const TAB_KEYS: TabKey[] = ['basic', 'affiliation', 'skills', 'compensation', 'income-cap', 'family'];

export function EmployeeDetailPage() {
  const { id = '' } = useParams();
  const user = useAuthStore((s) => s.user);
  // 他画面からの深いリンク (SkillRegistrationSection.tsx の「スキル登録を開く」、
  // docs/design/v05-skill-registration-table-redesign-detail.md §8-2 項目3) 用に初回描画時だけ
  // ?tab= を読む。TalentDashboardPage.tsx と同じパターン (以後のタブ操作はURLへ反映しない)。
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : 'basic');

  const { data: employee, isLoading, isError } = useQuery<EmployeeDetail>({
    queryKey: ['employee', id],
    queryFn: () => apiFetch(`/employees/${id}`),
    enabled: !!id,
  });

  const permsReady = usePermsReady();
  const canManageSkills = hasPermission(user?.effective_permissions, 'skill', 'manage');
  const canManageCompensation = hasPermission(user?.effective_permissions, 'compensation', 'manage');
  const canManageIncomeCap = hasPermission(user?.effective_permissions, 'income_cap', 'manage');
  // ⚠️ v04-leave-management-detail.md §3-4-2 / §13-2 ⑧: employee_family に own_store の
  // permission 行は存在しない (店長を締め出す設計)。hasPermission() は scope を見ないため、
  // R-STAFF が持つ `employee_family:view:self` (自分の家族情報用) だけで誤って true になってしまう。
  // hasNonSelfPermission() (stores/auth.ts、lib/navigation.ts の requiresNonSelfScope と共通の
  // 実装) で self scope のみの場合を除外する。「そもそも employee_family の権限を持っているか」は
  // hasPermission() が担い、両方を満たしたときだけタブを表示する。
  const canViewFamily = hasPermission(user?.effective_permissions, 'employee_family', 'view')
    && hasNonSelfPermission(user?.effective_permissions, ['employee_family']);

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'basic', label: '基本情報' },
    { key: 'affiliation', label: '所属' },
    { key: 'skills', label: 'スキル・人材像' },
    { key: 'compensation', label: '給与単価' },
    { key: 'income-cap', label: '年収の壁' },
    { key: 'family', label: '家族情報' },
  ];

  if (!id) return null;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/employees" className="inline-flex items-center gap-1 min-h-[44px] text-xs text-misespark-navy hover:underline">
          <ArrowLeft size={14} aria-hidden />社員管理に戻る
        </Link>
        {isLoading ? (
          <div className="text-text-sub mt-1">読み込み中…</div>
        ) : isError || !employee ? (
          <div className="p-4 border border-danger-border bg-danger-bg rounded-lg text-danger-text text-sm mt-2 flex items-center gap-2">
            <Lock size={14} aria-hidden />社員情報の取得に失敗しました。権限をご確認ください。
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <h1 className="text-h1">{formatEmployeeName(employee)}</h1>
            <StatusBadge status={employee.status} />
            <span className="text-xs text-text-sub font-mono">{employee.employee_number}</span>
          </div>
        )}
      </div>

      {employee && (
        <>
          <div className="border-b border-surface-border overflow-x-auto">
            <nav className="flex gap-1 min-w-max" aria-label="タブ">
              {tabs.map((t) => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)} aria-current={tab === t.key}
                  className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                    tab === t.key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
                  }`}>
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          {tab === 'basic' && <BasicInfoTab employee={employee} />}
          {tab === 'affiliation' && <AffiliationTab employeeId={employee.employee_id} />}
          {tab === 'skills' && <SkillMatrixEditor employeeId={employee.employee_id} canManage={canManageSkills} />}
          {tab === 'compensation' && <CompensationTab employeeId={employee.employee_id} canManage={canManageCompensation} />}
          {tab === 'income-cap' && (!permsReady
            ? <p role="status" className="text-sm text-text-sub">読み込み中…</p>
            : canManageIncomeCap
              ? <IncomeCapTab employeeId={employee.employee_id} />
              : <p className="text-sm text-text-sub">この社員の年収上限を管理する権限がありません。</p>)}
          {tab === 'family' && (!permsReady
            ? <p role="status" className="text-sm text-text-sub">読み込み中…</p>
            : canViewFamily
              ? <FamilyInfoTab employeeId={employee.employee_id} />
              : <p className="text-sm text-text-sub">この社員の家族情報を閲覧する権限がありません。</p>)}
        </>
      )}
    </div>
  );
}
